import {Fragment} from 'react';
import {assignInlineVars} from '@vanilla-extract/dynamic';

import {Text} from '#component/base/Text';
import {Transition} from '#component/util';

import {AnimatedTextProps} from './AnimatedText.types';

import * as css from './AnimatedText.css';

/**
 * This component is used to apply an animated entry/exit transition to
 * individual words within a block of text.
 *
 * This component renders a `Transition.Child` internally, and should be mounted
 * as a child of a `Transition`, which will control its visibility.
 */
export const AnimatedText = ({
  children,
  delay = 0,
  afterEnter,
  afterLeave,
  appear,
  beforeEnter,
  beforeLeave,
  enter,
  enterFrom,
  enterTo,
  entered,
  leave,
  leaveFrom,
  leaveTo,
  splitText = true,
  unmount,
  ...otherProps
}: AnimatedTextProps) => {
  const childrenArray = splitText && children ? children?.split(' ') : [children];
  const totalDelay = Math.max(0, 1500 + childrenArray.length * delay);

  return (
    <Text as="p" role="text" {...otherProps}>
      <Transition.Child
        afterEnter={afterEnter}
        afterLeave={afterLeave}
        appear={appear}
        as="span"
        beforeEnter={beforeEnter}
        beforeLeave={beforeLeave}
        className={css.root}
        enter={enter}
        enterFrom={enterFrom}
        enterTo={enterTo}
        entered={entered}
        leave={leave}
        leaveFrom={leaveFrom}
        leaveTo={leaveTo}
        style={assignInlineVars({[css.transitionDelay]: `${totalDelay}ms`})}
        unmount={unmount}>
        {childrenArray.map((child, i) => (
          <Fragment key={i}>
            <span className={css.word} style={{transitionDelay: `${delay + 20 * i}ms`}}>
              {child}
            </span>{' '}
          </Fragment>
        ))}
      </Transition.Child>
    </Text>
  );
};
